import { useMemo } from 'react';
import { Alert, Col, Row } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { Card, CardBody, CardHeader } from 'reactstrap';

import Loader from 'components/Loader';
import { useK2Loader } from 'hooks/useK2Loader';

import AdvancedTable from 'components/LM/common/AdvancedTable';
import Breadcrumb from "components/LM/common/Breadcrumb";
import ExportCSV from 'components/LM/common/ExportCSV';
import GoTopButton from 'components/LM/common/GoTopButton';

const TABLE_COLUMNS = [
	{ name: "item_id", alias: "Item ID" },
	{ name: "voice_link", alias: "Voice ID" },
	{ name: "voice_name", alias: "Voice name" },
	{ name: "status", alias: "Status" },
	{ name: "reason", alias: "Reason" },
	{ name: "message", alias: "Log message" }
];

const CSV_COLUMNS = [
	{ id: 'id', displayName: 'Id' },
	{ id: 'name', displayName: 'Voice name' },
	{ id: 'status', displayName: 'Status' },
	{ id: 'reason', displayName: 'Reason' },
	{ id: 'logMessage', displayName: 'LogMessage' }
];

function DetailStatus(props) {
	const { id } = props;

	const [loadingDetails, detailsResponse, errorDetails] = useK2Loader(`/voices/batch-request/${id}/items`);

	const renderTable = useMemo(() => {
		if (!detailsResponse) return null;

		return (
			<AdvancedTable
				hidePagination={true}
				columns={TABLE_COLUMNS}
				data={processApiResponse(detailsResponse)}
			/>
		);
	}, [detailsResponse]);

	if (loadingDetails) return <Loader />
	if (errorDetails) return <Alert variant="danger">{errorDetails.toString()}</Alert>

	return (
		<>
			{breadcrumbPage(detailsResponse.name)}
			<Card>
				<CardHeader>
					<Row>
						<Col>
							Name: <strong>{detailsResponse.name}</strong>
							<br />
							Requester: {detailsResponse.requester ?? "-"}
							<br />
							Date: {detailsResponse.inserted_at}
						</Col>
					</Row>
				</CardHeader>
				<CardBody>
					<div className="d-flex justify-content-end">
						<ExportCSV
							filename={detailsResponse.name}
							buttonText="Download results in CSV"
							columns={CSV_COLUMNS}
							datas={prepareCSVToDownload(detailsResponse.ingestBatchRequestItems)}
						/>
					</div>
					{renderTable}
				</CardBody>
			</Card>
			<GoTopButton />
		</>
	);
}

function processApiResponse(response) {
	const items = response.ingestBatchRequestItems;
	const total = items.length;
	const rows = items.map(item => {
		return {
			id: item.id,
			item_id: item.id,
			voice_link: item.voice.id ? <VoiceLink id={item.voice.id} /> : "-",
			voice_name: item.voice.name ?? "-",
			status: item.status,
			reason: item.reason ?? "-",
			message: <MessageList messages={item.messages} />
		}
	});

	return { total, rows };
}

function VoiceLink(props) {
	return <Link to={`/voices/${props.id}`} target="_blank" rel="noopener noreferrer">{props.id}</Link>
}

function MessageList(props) {
	const { messages } = props;
	if (!messages || messages.length === 0) return "-";
	return (
		<ul className="mb-0 ps-3">
			{messages.map(message => <li key={message}>{message}</li>)}
		</ul>
	);
}

function prepareCSVToDownload(items) {
	return items.map(item => {
		return {
			id: item.voice.id,
			name: item.voice.name,
			status: item.status,
			reason: item.reason ?? "",
			logMessage: item.messages.join(', ')
		};
	});
}

function breadcrumbPage(name) {
	const items = [
		"Voices",
		<a href="/voices/new-import">New Mass Import</a>,
		<a href="/voices/new-import/status">Import Status</a>,
		name
	];
	return <Breadcrumb items={items} />
}

export default DetailStatus;
